import useTranslation from 'next-translate/useTranslation'
import dynamic from 'next/dynamic'
import React, { MutableRefObject, useEffect, useRef, useState } from 'react'

import AnimatedButton from '@/components/AnimatedButton'
import { MarkerClicked } from '@/components/Modal/Types/Map'
import { ContextValue } from '@/context/Cities'
import { useDevice } from '@/context/Device'
import useIntersectionObserver from '@/hooks/useIntersectionObserver'
import { ListingCate } from '@/page-commons/listingBySlug'
import { MapPoi } from '@/types/poi'
import { isCityReferenceArea } from '@/utils/cities-mapper'
import { isEnter } from '@/utils/keyboard-helper'
import { isLocaleEnabledFor } from '@/utils/locale-variables'

import Nearby from './Nearby'
import { detailButton, poiSectionWrapper, spacer } from './style.css'

const StaticGoogleMapNoSsr = dynamic(() => import('@/components/StaticGoogleMap'), {
  ssr: false,
})

type Props = {
  cities: ContextValue
  closestPointsOfInterest: MapPoi[]
  handleTrackingCallAgency: (eventCategory: string) => void
  listing: ListingCate
  onMapModalOpen: (value: {
    event: React.SyntheticEvent<HTMLElement>
    eventCategory: string
    isReopening?: boolean
  }) => void
  onOpenDetailFormModal: (reopenMap: boolean) => (event: React.SyntheticEvent<HTMLElement>) => void
  onOpenMapModalWithoutEvent: (value: {
    eventCategory: string
    isReopening?: boolean
    selectedMarker?: MarkerClicked
  }) => void
  pointsOfInterest: MapPoi[]
  similarPropertiesRef: MutableRefObject<HTMLDivElement>
}

const SectionPoi: React.FC<Props> = (props) => {
  const device = useDevice()
  const { lang, t } = useTranslation()

  const currentCity = props.cities.list.find((city) => city.slug === props.listing.detail.city)

  const ref = useRef<HTMLDivElement | null>(null)
  const entry = useIntersectionObserver(ref, {})
  const [isStaticMapVisibile, setIsStaticMapVisibile] = useState(false)

  useEffect(() => {
    if (ref.current && !isStaticMapVisibile) {
      setIsStaticMapVisibile(entry?.isIntersecting)
    }
  }, [ref.current, entry?.isIntersecting])

  /** Hide points of interest from cities that don't yet implement this feature */
  if (!isCityReferenceArea(currentCity)) {
    return null
  }

  const mapHeight = device === 'mobile' ? 250 : 320

  return (
    <>
      <div
        ref={ref}
        className={poiSectionWrapper}
        data-testid="section-poi"
        role="button"
        tabIndex={0}
        onClick={(event) => props.onMapModalOpen({ event, eventCategory: 'ListingDetailMap' })}
        onKeyPress={(e) => {
          if (isEnter(e)) {
            props.onMapModalOpen({ event: e, eventCategory: 'ListingDetailMap' })
          }
        }}
      >
        {isStaticMapVisibile ? (
          <>
            <AnimatedButton className={detailButton}>{t('common:open-map')}</AnimatedButton>
            <StaticGoogleMapNoSsr
              coordinates={props.listing.coordinates}
              handleOpenMapModal={(markerClicked) =>
                props.onOpenMapModalWithoutEvent({
                  eventCategory: 'ListingDetailMap',
                  selectedMarker: markerClicked,
                })
              }
              height={mapHeight}
              pointsOfInterest={isLocaleEnabledFor('POIs', lang) ? props.closestPointsOfInterest : []}
            />
          </>
        ) : (
          <div className={spacer}></div>
        )}
      </div>
      {isLocaleEnabledFor('POIs', lang) && props.closestPointsOfInterest.length > 0 && (
        <Nearby
          cities={props.cities}
          closestPointsOfInterest={props.closestPointsOfInterest}
          handleTrackingCallAgency={props.handleTrackingCallAgency}
          listing={props.listing}
          pointsOfInterest={props.pointsOfInterest}
          similarPropertiesRef={props.similarPropertiesRef}
          onOpenDetailFormModal={props.onOpenDetailFormModal}
        />
      )}
    </>
  )
}

export default SectionPoi
